import React, { useState, useContext } from "react";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Breadcrumbs,
  Container,
  Link,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Link as RouterLink } from "react-router";

import { USER, ORGANIZATIONS } from "src/platform/constants/routes";
import { NotificationContext } from "src/shell/contexts/notificationContext";
import { getContainerStyles } from "src/shell/styles/themeUtils";

import PreferencesSettings from "./PreferencesSettings";
import SecuritySettings from "./SecuritySettings";
import type { UserSettings } from "./settings-helpers";

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Local security-only state for the page. Notification preferences are now
 * loaded from the API via `useNotificationPreferences()` inside
 * `PreferencesSettings`; only the security section is still managed locally
 * here (no backend endpoint yet). `SecuritySettings` expects the full
 * `UserSettings` shape, so we keep placeholder values for the unused slices.
 */
const DEFAULT_SETTINGS: UserSettings = {
  id: "",
  notifications: { email: false, push: false, sms: false, marketing: false },
  security: {
    twoFactorEnabled: false,
    sessionTimeout: 30,
    passwordExpiryDays: 90,
  },
  preferences: { theme: "auto", language: "en", timezone: "UTC" },
};

const UserSettingsPage: React.FC = () => {
  const theme = useTheme();
  const { setSnackBarNotification } = useContext(NotificationContext);
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordForm>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSecurityChange = (
    key: keyof UserSettings["security"],
    value: boolean | number,
  ) => {
    setSettings((prev) => ({
      ...prev,
      security: { ...prev.security, [key]: value },
    }));
  };

  const handleChangePassword = async () => {
    try {
      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        throw new Error("New passwords do not match");
      }

      if (passwordForm.newPassword.length < 8) {
        throw new Error("Password must be at least 8 characters long");
      }

      // TODO (backend): replace with real change-password API call.
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setPasswordDialogOpen(false);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setSnackBarNotification({
        message: "Password changed successfully",
        severity: "success",
      });
    } catch (err) {
      setSnackBarNotification({
        message: err instanceof Error ? err.message : "Error changing password",
        severity: "error",
      });
    }
  };

  return (
    <Container sx={getContainerStyles(theme, "lg")}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          component={RouterLink}
          to={ORGANIZATIONS.ROOT.path}
          color="inherit"
          underline="hover"
          variant="body2"
        >
          Organizations
        </Link>
        <Link
          component={RouterLink}
          to={USER.PROFILE.path}
          color="inherit"
          underline="hover"
          variant="body2"
        >
          Profile
        </Link>
        <Typography variant="body2" color="text.primary">
          Settings
        </Typography>
      </Breadcrumbs>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Account Settings
      </Typography>

      <Stack spacing={3}>
        {/* Notification preferences — loads + saves via the notification API. */}
        <PreferencesSettings />

        <SecuritySettings
          settings={settings}
          onSecurityChange={handleSecurityChange}
          passwordDialogOpen={passwordDialogOpen}
          onPasswordDialogClose={() => setPasswordDialogOpen(false)}
          passwordForm={passwordForm}
          onPasswordFormChange={setPasswordForm}
          onChangePassword={() => void handleChangePassword()}
        />
      </Stack>
    </Container>
  );
};

export default UserSettingsPage;
